import { Grid, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FieldText from '../../../components/general/fields/fieldText';
import AutocompleteField from '../../../components/general/fields/autocompleteField';
import DropZoneField from '../../../components/general/fields/dropzoneField';
import { FieldTextWithInfoTooltip } from '../../../../shared/components/fieldTextWithInfoTooltip';
import { formatOrbitSelect, getOptionLabel } from '../utils/formatOrbitSelect';
import { formatOrbitPreferredContactLabel } from '../../../../shared/utils/orbitCaseDetail.util';
import { PqrDetailTooltipContent } from '../../../../shared/components/pqrDetailTooltipContent';
import { OrbitSectionHeader } from '../components/orbitSectionHeader';
import { useOrbitPqrInfo } from './hook';
import { OrbitPqrInfoProps } from '../sectionProps';
import { formatParams } from '../../../../shared/utils/params';

export const OrbitPqrInfo = (props: OrbitPqrInfoProps) => {
    const { readOnly } = props;
    const {
        formik,
        preferredContactMethods,
        caseTypes,
        loadingCaseTypes,
        caseTypesError,
        countries,
        loadingCountries,
        countriesError,
        departments,
        cities,
        loadingDepartments,
        loadingCities,
        departmentsError,
        citiesError,
        isColombia,
        handleAttachmentsChange,
    } = useOrbitPqrInfo(props);

    const countryLabel = readOnly
        ? formik.values.country
        : getOptionLabel(formatOrbitSelect(countries), formik.values.country) ||
          formik.values.country;

    const departmentLabel = readOnly
        ? formik.values.department
        : getOptionLabel(departments, formik.values.department) ||
          formik.values.department;

    const cityLabel = readOnly
        ? formik.values.city
        : getOptionLabel(cities, formik.values.city) || formik.values.city;

    const caseTypeLabel =
        getOptionLabel(formatOrbitSelect(caseTypes), formik.values.caseType) ||
        formik.values.caseType;

    return (
        <Grid
            container
            spacing={{ xs: 1.5, md: 1.5 }}
            columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
            pt="20px"
        >
            <Grid item xs={12}>
                <OrbitSectionHeader title="Información PQR" />
            </Grid>

            <Grid item xs={12} md={4}>
                {readOnly ? (
                    <FieldText
                        label="País"
                        name="country"
                        size="small"
                        disabled
                        value={countryLabel}
                    />
                ) : (
                    <AutocompleteField
                        data={formatOrbitSelect(countries)}
                        label="País"
                        name="country"
                        size="small"
                        required
                        disabled={
                            loadingCountries ||
                            Boolean(countriesError) ||
                            countries.length === 0
                        }
                        placeholder={
                            loadingCountries
                                ? 'Cargando países...'
                                : countriesError
                                    ? 'Catálogo no disponible'
                                    : undefined
                        }
                        onBlur={formik.handleBlur}
                        touched={
                            formik.touched.country || Boolean(countriesError)
                        }
                        errors={formik.errors.country || countriesError}
                        value={formik.values.country}
                        onChangeValue={(value: string) =>
                            formik.setFieldValue('country', value)
                        }
                    />
                )}
            </Grid>
            {isColombia && (
                <>
                    <Grid item xs={12} md={4}>
                        {readOnly ? (
                            <FieldText
                                label="Departamento"
                                name="department"
                                size="small"
                                disabled
                                value={departmentLabel}
                            />
                        ) : (
                            <AutocompleteField
                                data={departments}
                                label="Departamento"
                                name="department"
                                size="small"
                                required
                                disabled={
                                    loadingDepartments ||
                                    Boolean(departmentsError) ||
                                    departments.length === 0
                                }
                                placeholder={
                                    loadingDepartments
                                        ? 'Cargando departamentos...'
                                        : departmentsError
                                            ? 'Catálogo no disponible'
                                            : 'Seleccionar departamento...'
                                }
                                onBlur={formik.handleBlur}
                                touched={
                                    formik.touched.department ||
                                    Boolean(departmentsError)
                                }
                                errors={
                                    formik.errors.department || departmentsError
                                }
                                value={formik.values.department}
                                onChangeValue={(value: string) =>
                                    formik.setFieldValue('department', value)
                                }
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {readOnly ? (
                            <FieldText
                                label="Ciudad"
                                name="city"
                                size="small"
                                disabled
                                value={cityLabel}
                            />
                        ) : (
                            <AutocompleteField
                                data={cities}
                                label="Ciudad"
                                name="city"
                                size="small"
                                required
                                disabled={
                                    !formik.values.department ||
                                    loadingCities ||
                                    Boolean(citiesError) ||
                                    cities.length === 0
                                }
                                placeholder={
                                    !formik.values.department
                                        ? 'Seleccione departamento primero'
                                        : loadingCities
                                            ? 'Cargando ciudades...'
                                            : citiesError
                                                ? 'Catálogo no disponible'
                                                : 'Seleccionar ciudad...'
                                }
                                onBlur={formik.handleBlur}
                                touched={
                                    formik.touched.city || Boolean(citiesError)
                                }
                                errors={formik.errors.city || citiesError}
                                value={formik.values.city}
                                onChangeValue={(value: string) =>
                                    formik.setFieldValue('city', value)
                                }
                            />
                        )}
                    </Grid>
                </>
            )}

            <Grid item xs={12} md={4}>
                {readOnly ? (
                    <FieldText
                        label="Tipo de Caso PQRS"
                        name="caseType"
                        size="small"
                        disabled
                        value={caseTypeLabel}
                    />
                ) : (
                    <AutocompleteField
                        data={formatOrbitSelect(caseTypes)}
                        label="Tipo de Caso PQRS"
                        name="caseType"
                        size="small"
                        required
                        disabled={
                            loadingCaseTypes ||
                            Boolean(caseTypesError) ||
                            caseTypes.length === 0
                        }
                        placeholder={
                            loadingCaseTypes
                                ? 'Cargando tipos de caso...'
                                : caseTypesError
                                    ? 'Catálogo no disponible'
                                    : 'Seleccionar tipo de caso...'
                        }
                        onBlur={formik.handleBlur}
                        touched={
                            formik.touched.caseType || Boolean(caseTypesError)
                        }
                        errors={formik.errors.caseType || caseTypesError}
                        value={formik.values.caseType}
                        onChangeValue={(value: string) =>
                            formik.setFieldValue('caseType', value)
                        }
                    />
                )}
            </Grid>

            <Grid item xs={12}>
                <FieldTextWithInfoTooltip
                    label="Detalle de PQR"
                    name="detail"
                    placeholder="Describa detalladamente su solicitud"
                    size="small"
                    required
                    rows={5}
                    inputProps={{ maxLength: 4500 }}
                    disabled={readOnly}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.detail}
                    errors={formik.errors.detail}
                    value={formik.values.detail}
                    onChange={formik.handleChange}
                    infoTooltipWidth={650}
                    infoTooltipText={<PqrDetailTooltipContent />}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                {readOnly ? (
                    <FieldText
                        label="Medio de Respuesta"
                        name="preferredContactMethod"
                        size="small"
                        required
                        disabled
                        value={formatOrbitPreferredContactLabel(
                            formik.values.preferredContactMethod
                        )}
                    />
                ) : (
                    <AutocompleteField
                        data={formatParams(preferredContactMethods)}
                        label="Medio de Respuesta"
                        name="preferredContactMethod"
                        size="small"
                        required
                        onBlur={formik.handleBlur}
                        touched={formik.touched.preferredContactMethod}
                        errors={formik.errors.preferredContactMethod}
                        value={formik.values.preferredContactMethod}
                        onChangeValue={(value: string) =>
                            formik.setFieldValue(
                                'preferredContactMethod',
                                value
                            )
                        }
                    />
                )}
            </Grid>
            <Grid
                item
                xs={12}
                md={8}
                sx={{ display: 'flex', alignItems: 'center' }}
            >
                <Typography style={{ fontWeight: 'bold', color: '#000' }}>
                    El trámite de respuesta a su solicitud no tendrá ningún
                    costo
                </Typography>
            </Grid>

            {!readOnly && (
                <Grid item xs={12}>
                    <Typography sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
                        Adjuntar Archivos
                    </Typography>
                    <DropZoneField
                        key={props.attachmentsResetKey ?? 0}
                        name="attachments"
                        title="Suelte los archivos aquí o haga click para subir"
                        subtitle="(Máximo 30MB)"
                        maxFileSize={30 * 1024 * 1024}
                        onChange={handleAttachmentsChange}
                        icon
                    >
                        <CloudUploadOutlinedIcon
                            sx={{ color: 'primary.main', fontSize: '3.5rem' }}
                        />
                    </DropZoneField>
                </Grid>
            )}
        </Grid>
    );
};
